import React, { useEffect, useState } from "react";
import { ChartProps } from "./Chart";
import InCHlibX from "inchlibx";
import { useSelector } from "react-redux";
import { getHeatmapData } from "../api/heatmap";
import {
  Button,
  Typography,
  Box,
  Stack,
  Paper,
  Divider,
  Slider,
  ToggleButton,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  ToggleButtonGroup,
  Chip
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import DownloadIcon from "@mui/icons-material/Download";
import TuneIcon from "@mui/icons-material/Tune";
import axios from "axios";

const dataBaseUrl = "https://api.plantgenie.org/exheatmap/tmp/";

const ROW_DISTANCE_VALUES = [
  { label: "Euclidean", value: "euclidean" },
  { label: "Braycurtis", value: "braycurtis" },
  { label: "Correlation", value: "correlation" },
  { label: "Canberra", value: "canberra" },
  { label: "Cosine", value: "cosine" },
  { label: "Chebyshev", value: "chebyshev" },
  { label: "Cityblock", value: "cityblock" },
  { label: "Mahalanobis", value: "mahalanobis" },
  { label: "Minkowski", value: "minkowski" },
  { label: "Seuclidean", value: "seuclidean" },
  { label: "Sqeuclidean", value: "sqeuclidean" },
  { label: "Dice", value: "dice" },
  { label: "Hamming", value: "hamming" },
  { label: "Jaccard", value: "jaccard" },
  { label: "Kulsinski", value: "kulsinski" },
  { label: "Rogerstanimoto", value: "rogerstanimoto" },
  { label: "Russellrao", value: "russellrao" },
  { label: "Sokalmichener", value: "sokalmichener" },
  { label: "Sokalsneath", value: "sokalsneath" },
  { label: "Yule", value: "yule" },
];

const ROW_LINKAGE_VALUES = [
  { label: "Ward", value: "ward" },
  { label: "Single", value: "single" },
  { label: "Complete", value: "complete" },
  { label: "Average", value: "average" },
  { label: "Centroid", value: "centroid" },
  { label: "Median", value: "median" },
  { label: "Weighted", value: "weighted" },
];

const prepareCsvFromHeatMapData = (file) => {
  const columns = ["Gene name", ...file?.data?.feature_names];
  const rows: any = [];
  const keys = Object.keys(file.data.nodes);
  for (let i = 0; i < keys.length; i++) {
    const node = file.data.nodes[keys[i]];
    if (node?.features) {
      const row = [node.objects[0], ...node.features];
      rows.push(row);
    }
  }
  let csvContent = "data:text/csv;charset=utf-8," + columns.join(",") + "\n";
  csvContent += rows.map((e) => e.join(",")).join("\n");
  var encodedUri = encodeURI(csvContent);
  var link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "heatmap_data.csv");
  document.body.appendChild(link);
  link.click();
};

const NeoHeatMap = (props: ChartProps) => {
  const [fileLink, setFileLink] = useState("");
  const [inchlib, setInchlib] = useState<any>(null);
  const [normalized, setNormalized] = useState(true);
  const [axis, setAxis] = useState("row");
  const [sliderValue, setSliderValue] = useState(10);
  const [showCellValues, setShowCellValues] = useState(false);
  const [rowDistance, setRowDistance] = useState("euclidean");
  const [rowLinkage, setRowLinkage] = useState("ward");

  const { getGlobalParameter } = props;

  const applicationState = useSelector(
    (state: any) => state.dashboard?.settings?.parameters
  );

  const connectionState = useSelector((state: any) => state?.application?.connection);

  async function getFileData(file) {
    try {
      const response = await axios.get(file);
      if (response.status === 200) {
        prepareCsvFromHeatMapData(response.data);
      }
    } catch (error) { }
  }

  async function getFileLink() {
    const fileName = getGlobalParameter
      ? getGlobalParameter("neodash_experiment_value")
      : null;

    try {
      if (!fileName || !applicationState?.neodash_genelist_name) return;
      const response = await getHeatmapData(
        applicationState?.neodash_genelist_name.join(","),
        fileName,
        normalized,
        axis,
        rowDistance,
        rowLinkage,
        connectionState?.database
      );

      if (response?.data?.link.length === 13) {
        setFileLink(dataBaseUrl + response.data.link + ".json");
      }
    } catch (error) { }
  }

  const handleChange = (event, newValue) => {
    setSliderValue(newValue);
  };

  const handleChangeNormalized = (event, normalized) => {
    setNormalized(normalized);
  };

  const handleChangeAxis = (event, axis) => {
    setAxis(axis);
  };

  const handleChangeShowCellValues = (event, showCellValues) => {
    setShowCellValues(showCellValues);
  };

  const handleChangeRowDistance = (event) => {
    setRowDistance(event.target.value);
  };

  const handleChangeRowLinkage = (event) => {
    setRowLinkage(event.target.value);
  };

  useEffect(() => {
    getFileLink();
  }, [applicationState, normalized, axis, rowDistance, rowLinkage]);

  useEffect(() => {
    if (!fileLink) return;

    const inchlib = new InCHlibX({
      target: "inchlib",
      width: 100 * sliderValue,
      max_height: props?.dimensions?.height,
      heatmap_colors: "BuWhRd",
      metadata: true,
      independent_columns: false,
      column_metadata: true,
      metadata_colors: "Reds",
      heatmap_part_width: 0.9,
      show_cell_values: showCellValues,
      draw_row_ids: true,
    });
    inchlib.read_data_from_file(fileLink);
    inchlib.draw();
    inchlib.hide_navigation_layer();
    setInchlib(inchlib);
  }, [props, fileLink, sliderValue, showCellValues]);

  const downloadImage = () => {
    const fileName = prompt("Please enter file name", "inchlib");
    if (fileName == null || fileName == "") {
      alert("Please enter file name");
    }
    inchlib.export_image(fileName);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#fafafa' }}>
      {/* Heatmap Visualization - Top Section */}
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          bgcolor: 'white',
          p: 2,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <div id="inchlib"></div>
      </Box>

      {/* Control Panel - Bottom Section */}
      {inchlib && (
        <Box
          sx={{
            p: 3,
            bgcolor: 'white',
            borderTop: '1px solid #e0e0e0'
          }}
        >
          {/* Export Actions */}
          <Stack
            direction="row"
            spacing={2}
            sx={{ mb: 3 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Heatmap Controls
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => getFileData(fileLink)}
                sx={{ textTransform: 'none' }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveAltIcon />}
                onClick={downloadImage}
                sx={{ textTransform: 'none' }}
              >
                Export PNG
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* Visualization Settings */}
          <Stack spacing={3}>
            {/* Width Control */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                Heatmap Width
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Slider
                  value={sliderValue}
                  onChange={handleChange}
                  min={5}
                  max={20}
                  step={1}
                  marks
                  valueLabelDisplay="auto"
                  sx={{ flex: 1 }}
                />
                <Chip label={`${sliderValue}x`} color="primary" variant="outlined" />
              </Stack>
            </Box>

            {/* Data Transformation & Clustering */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#555' }}>
                Data & Clustering Options
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup
                  color="primary"
                  value={normalized}
                  size="small"
                  exclusive
                  onChange={handleChangeNormalized}
                >
                  <ToggleButton value={true} sx={{ textTransform: 'none', px: 2 }}>
                    Absolute log2(TPM+1)
                  </ToggleButton>
                  <ToggleButton value={false} sx={{ textTransform: 'none', px: 2 }}>
                    Row Scaled
                  </ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  color="primary"
                  value={axis}
                  size="small"
                  exclusive
                  onChange={handleChangeAxis}
                >
                  <ToggleButton value="row" sx={{ textTransform: 'none', px: 2 }}>
                    Cluster Genes Only
                  </ToggleButton>
                  <ToggleButton value="both" sx={{ textTransform: 'none', px: 2 }}>
                    Cluster Genes & Samples
                  </ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  color="primary"
                  value={showCellValues}
                  size="small"
                  exclusive
                  onChange={handleChangeShowCellValues}
                >
                  <ToggleButton value={true} sx={{ textTransform: 'none', px: 2 }}>
                    Show Values
                  </ToggleButton>
                  <ToggleButton value={false} sx={{ textTransform: 'none', px: 2 }}>
                    Hide Values
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>

            {/* Distance & Linkage Methods */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#555' }}>
                Clustering Parameters
              </Typography>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Distance Method</InputLabel>
                  <Select
                    value={rowDistance}
                    label="Distance Method"
                    onChange={handleChangeRowDistance}
                  >
                    {ROW_DISTANCE_VALUES.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Linkage Method</InputLabel>
                  <Select
                    value={rowLinkage}
                    label="Linkage Method"
                    onChange={handleChangeRowLinkage}
                  >
                    {ROW_LINKAGE_VALUES.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default NeoHeatMap;